import { isNil, maxBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { getContactNameFromObject } from '../contact/contactutils';
import { ACCOUNT_LABEL_TABLE_NAME, getClientId, getReadId, getTableClientId } from '../databaseUtils';
import { makeLabels } from '../labelsUtils';
import {
  AccountLabel,
  AccountLabelCustomUsage,
  BankAccount,
  BankAccountDTO,
  Contact,
  CreateAccountLabelInput,
  Language,
} from '../types';

import { ACCOUNT_BANK_TOPCLASS, ACCOUNT_CONTRACTOR_TOPCLASS, ACCOUNT_OWNERS_TOPCLASS } from './accountClasses';
import { getAccountLabelsFor } from './accountLabelUtils';

export type CreateAccountLabelFct = (input: CreateAccountLabelInput) => Promise<AccountLabel | undefined>;
export type ExistingOrNotAccountLabel =
  | { isExisting: true; accountLabel: AccountLabel }
  | { isExisting: false; accountLabel: CreateAccountLabelInputWithId };
export interface CreateAccountLabelInputWithId extends Omit<CreateAccountLabelInput, 'id'> {
  id: string;
}

const REQUEST_FOR_FUNDS_LABELS = makeLabels(
  'Request for Funds',
  'Demande de fonds',
  'Verzoek om fondsen',
  'Antrag auf Mittel'
);

const getClassForNewAccountLabel = (accountLabels: AccountLabel[], topClass: number) => {
  const selectedAccountLabels = accountLabels.filter(
    (currentAccountLabel) => currentAccountLabel.topClass === topClass
  );
  const accountLabelWithMaxClass = maxBy(selectedAccountLabels, 'class');
  if (isNil(accountLabelWithMaxClass)) {
    return topClass + 1;
  }
  return accountLabelWithMaxClass.class + 1;
};

export const getOrBuildAccountLabelForBankAccount = (
  bankAccount: BankAccount | BankAccountDTO,
  accountLabels: AccountLabel[],
  ownerId?: string | null
): ExistingOrNotAccountLabel => {
  const accountLabelsByOwner = getAccountLabelsFor(accountLabels, ownerId);
  const accountLabelForBankAccount = accountLabelsByOwner.find(
    (accountLabel) => accountLabel.bankAccountId === bankAccount.id
  );
  if (!isNil(accountLabelForBankAccount)) {
    return { isExisting: true, accountLabel: accountLabelForBankAccount };
  }
  const bankAccountName = `${getContactNameFromObject(bankAccount.contact)} - ${bankAccount.number}`;
  const clientId = getClientId(bankAccount.clientId);
  const shouldAddOwnerId = accountLabelsByOwner.some((accountLabel) => !isNil(accountLabel.ownerId));
  const accountLabel = {
    id: uuidv4(),
    class: getClassForNewAccountLabel(accountLabels, ACCOUNT_BANK_TOPCLASS),
    topClass: ACCOUNT_BANK_TOPCLASS,
    bankAccountId: bankAccount.id,
    labels: makeLabels(bankAccountName, bankAccountName, bankAccountName, bankAccountName),
    ownerId: shouldAddOwnerId ? ownerId : undefined,
    readId: getReadId(clientId, ACCOUNT_LABEL_TABLE_NAME),
    clientId: getTableClientId(clientId, ACCOUNT_LABEL_TABLE_NAME),
  };

  return { isExisting: false, accountLabel };
};

export const getOrCreateAccountLabelForBankAccount = async (
  bankAccount: BankAccount | BankAccountDTO,
  accountLabels: AccountLabel[],
  createAccountLabel: CreateAccountLabelFct,
  ownerId?: string | null
) =>
  await getOrCreateAccountLabel(
    getOrBuildAccountLabelForBankAccount(bankAccount, accountLabels, ownerId),
    createAccountLabel
  );

export const getOrBuildAccountLabelForContractor = (
  contactId: string,
  clientId: string,
  accountLabels: AccountLabel[]
): ExistingOrNotAccountLabel => {
  const accountForContractor = accountLabels.find(
    (accountLabel) => accountLabel.contactId === contactId && accountLabel.topClass === ACCOUNT_CONTRACTOR_TOPCLASS
  );
  if (!isNil(accountForContractor)) {
    return { isExisting: true, accountLabel: accountForContractor };
  }
  const accountLabel = {
    id: uuidv4(),
    class: getClassForNewAccountLabel(accountLabels, ACCOUNT_CONTRACTOR_TOPCLASS),
    topClass: ACCOUNT_CONTRACTOR_TOPCLASS,
    contactId: contactId,
    labels: makeLabels(
      `Supplier ${contactId}`,
      `Fournisseur ${contactId}`,
      `Leverancier ${contactId}`,
      `Lieferanten ${contactId}`
    ),
    readId: getReadId(clientId, ACCOUNT_LABEL_TABLE_NAME),
    clientId: getTableClientId(clientId, ACCOUNT_LABEL_TABLE_NAME),
  };

  return { isExisting: false, accountLabel };
};

export const getOrCreateAccountLabelForContractor = async (
  contactId: string,
  clientId: string,
  accountLabels: AccountLabel[],
  createAccountLabel: CreateAccountLabelFct
) =>
  await getOrCreateAccountLabel(
    getOrBuildAccountLabelForContractor(contactId, clientId, accountLabels),
    createAccountLabel
  );

export const getOrBuildAccountLabelForOwner = (
  owner: Contact,
  requestForFunds: boolean,
  accountLabels: AccountLabel[]
): ExistingOrNotAccountLabel => {
  const ownerAccountLabel = accountLabels.find(
    (accountLabel) =>
      accountLabel.contactId === owner.id &&
      accountLabel.topClass === ACCOUNT_OWNERS_TOPCLASS &&
      accountLabel.customUsage === (requestForFunds ? AccountLabelCustomUsage.OWNER_REQUEST_FOR_FUNDS : undefined)
  );
  if (!isNil(ownerAccountLabel)) {
    return { isExisting: true, accountLabel: ownerAccountLabel };
  }

  const contactAccountLabel = getContactNameFromObject(owner);
  const clientId = getClientId(owner.clientId);
  const accountLabel = {
    id: uuidv4(),
    class: ACCOUNT_OWNERS_TOPCLASS,
    topClass: ACCOUNT_OWNERS_TOPCLASS,
    contactId: owner.id,
    labels: requestForFunds
      ? REQUEST_FOR_FUNDS_LABELS
      : Object.keys(Language).map((lang) => ({
          language: lang,
          label: contactAccountLabel,
        })),
    readId: getReadId(clientId, ACCOUNT_LABEL_TABLE_NAME),
    clientId: getTableClientId(clientId, ACCOUNT_LABEL_TABLE_NAME),
    customUsage: requestForFunds ? AccountLabelCustomUsage.OWNER_REQUEST_FOR_FUNDS : undefined,
  };
  return { isExisting: false, accountLabel };
};

export const getOrCreateAccountLabelForOwner = async (
  owner: Contact,
  requestForFunds: boolean,
  accountLabels: AccountLabel[],
  createAccountLabel: CreateAccountLabelFct
) =>
  await getOrCreateAccountLabel(
    getOrBuildAccountLabelForOwner(owner, requestForFunds, accountLabels),
    createAccountLabel
  );

export const getOrCreateAccountLabel = async (
  result: ExistingOrNotAccountLabel,
  createAccountLabel: CreateAccountLabelFct
) => {
  if (result.isExisting) {
    return result.accountLabel;
  }
  return await createAccountLabel(result.accountLabel);
};
